import React, { useState, useMemo } from 'react';
import { Table, Button, Modal, message } from 'antd-v5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { SettingsContent } from '../../components/SettingsContent';
import MassMessageModal from './MassMessageModal';
import { useEventMassMessages, useArchiveMassMessage } from '../../../../../hooks/query/useMassMessages';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

const TOKEN_LABELS = {
  ':name:': 'Client Name',
  ':associate:': 'Associate Name',
  ':assigned_associate_name:': 'Associate Name',
  ':store:': 'Store Name',
  ':merchant:': 'Merchant Name',
  ':appointment_date:': 'Appointment Date',
  ':appointment_time:': 'Appointment Time',
};

const STATUS_LABELS = {
  COMPLETED: 'COMPLETED',
  PROCESSING: 'PROCESSING',
  CANCELED: 'CANCELED',
  FAILED: 'FAILED',
  PAUSED: 'PAUSED',
};

const convertTokensToLabels = text => {
  if (!text) return '';
  return text.replace(
    /:name:|:associate:|:assigned_associate_name:|:store:|:merchant:|:appointment_date:|:appointment_time:/g,
    match => TOKEN_LABELS[match] || match,
  );
};

const formatSchedule = (dateStr, tz) => {
  if (!dateStr) return '—';
  const d = tz ? dayjs(dateStr).tz(tz) : dayjs(dateStr);
  return d.format('D MMM YYYY [at] h:mma z');
};

const MassMessageList = ({ eventId, audienceId, audienceCount }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);

  const { data: massMessagesData, isLoading } = useEventMassMessages({ eventId });
  const archiveMutation = useArchiveMassMessage();

  const massMessages = useMemo(() => {
    if (!massMessagesData) return [];
    return Array.isArray(massMessagesData) ? massMessagesData : massMessagesData.data || [];
  }, [massMessagesData]);

  const handleAdd = () => {
    setEditingMessage(null);
    setModalOpen(true);
  };

  const handleEdit = record => {
    setEditingMessage(record);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingMessage(null);
  };

  const handleDelete = record => {
    const status = (record.status || '').toUpperCase();
    const isActive = status === 'PROCESSING' || status === 'PAUSED';

    Modal.confirm({
      title: isActive ? 'Cancel Message' : 'Cancel Scheduled Message',
      content: isActive
        ? 'This message is currently being processed. Canceling it will stop delivery to any remaining recipients. This action cannot be undone.'
        : 'Are you sure you want to cancel this scheduled message? It will not be sent to your audience.',
      okText: 'Yes, Cancel',
      okType: 'danger',
      cancelText: 'No, Keep It',
      onOk: async () => {
        try {
          await archiveMutation.mutateAsync(record.id);
          message.success('Message canceled');
        } catch (error) {
          console.error('Error canceling mass message:', error);
          message.error('Failed to cancel message');
        }
      },
    });
  };

  const DELETABLE = new Set(['SCHEDULED', 'PROCESSING', 'PAUSED']);

  const columns = [
    {
      title: 'Message Preview',
      dataIndex: 'message',
      key: 'message',
      render: text => {
        const converted = convertTokensToLabels(text);
        const truncated = converted.length > 100 ? `${converted.substring(0, 100)}...` : converted;
        return <span style={{ fontSize: 13 }}>{truncated}</span>;
      },
    },
    {
      title: 'Delivery Schedule',
      dataIndex: 'scheduledDateTime',
      key: 'scheduledDateTime',
      width: 220,
      render: (text, record) => <span style={{ fontSize: 13 }}>{formatSchedule(text, record.timezone)}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: text => {
        const status = (text || '').toUpperCase();
        const label = STATUS_LABELS[status] || status;
        return <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.45)' }}>{label}</span>;
      },
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      align: 'right',
      render: (_, record) => {
        const status = (record.status || '').toUpperCase();
        const canEdit = status === 'SCHEDULED';
        const canDelete = DELETABLE.has(status);

        if (!canEdit && !canDelete) return null;

        return (
          <div className="mass-message-actions">
            {canEdit && (
              <Button size="small" onClick={() => handleEdit(record)}>
                Edit
              </Button>
            )}
            {canDelete && (
              <Button
                size="small"
                type="text"
                danger
                onClick={() => handleDelete(record)}
                icon={<FontAwesomeIcon icon={['far', 'trash-alt']} />}
              />
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <SettingsContent
        icon={<FontAwesomeIcon icon={['far', 'paper-plane']} />}
        title="MESSAGE YOUR AUDIENCE"
        className="full-width-section"
        contentClassName="w-100-P"
        action={
          <Button onClick={handleAdd} icon={<FontAwesomeIcon icon={['fal', 'plus']} />}>
            Add Message
          </Button>
        }
      >
        {massMessages.length > 0 && (
          <Table
            className="mass-message-table"
            dataSource={massMessages}
            columns={columns}
            rowKey="id"
            loading={isLoading}
            pagination={false}
            size="small"
            showHeader
            locale={{ emptyText: 'No scheduled messages yet' }}
            style={{ width: '100%' }}
          />
        )}
      </SettingsContent>

      <MassMessageModal
        open={modalOpen}
        onClose={handleCloseModal}
        eventId={eventId}
        audienceId={audienceId}
        audienceCount={audienceCount}
        editingMessage={editingMessage}
      />
    </>
  );
};

export default MassMessageList;
